import { formatQuota } from 'utils/pricing';
import { absolute } from 'utils/url';

// Entity ids keep the `https://placekit.io/#...` form Google already knows.
// They are built from a fixed `${origin}/#...` string, not from absolute(),
// which drops the home trailing slash for canonical, hreflang and the sitemap.
const ORIGIN = absolute('/');

export const ORGANIZATION_ID = `${ORIGIN}/#organization`;
export const WEBSITE_ID = `${ORIGIN}/#website`;
export const SOFTWARE_ID = `${ORIGIN}/#software`;

/**
 * Organization node, referenced by the other nodes through `@id`.
 * @return {object}
 */
export const organization = () => ({
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'PlaceKit',
  url: absolute('/'),
  logo: absolute('/icon.svg'),
  description:
    'PlaceKit is a worldwide geocoding API providing fast and accurate address autocomplete, store locator, and two-way geocoding.',
  foundingDate: '2022',
  founders: [
    { '@type': 'Person', name: 'Raphaël Daguenet' },
    { '@type': 'Person', name: 'Nicolas Torres' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Paris',
    addressCountry: 'FR',
  },
  sameAs: [
    'https://github.com/placekit',
    'https://twitter.com/placekitapi',
    'https://linkedin.com/company/placekit',
    'https://www.producthunt.com/products/placekit',
    'https://dev.to/placekit',
  ],
});

/**
 * WebSite node. One entity for the whole site, so `inLanguage` lists every
 * language the site is published in rather than varying per page (page-level
 * language lives in `<html lang>`, hreflang and the page meta).
 * @return {object}
 */
export const website = () => ({
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'PlaceKit',
  url: absolute('/'),
  publisher: { '@id': ORGANIZATION_ID },
  inLanguage: ['en', 'fr'],
});

/**
 * SoftwareApplication node describing the API product, with its free tier.
 * One product entity shared by every page and language, hence the constant
 * `@id` and the single English description.
 * @return {object}
 */
export const softwareApplication = () => ({
  '@type': 'SoftwareApplication',
  '@id': SOFTWARE_ID,
  name: 'PlaceKit Geocoding API',
  url: absolute('/'),
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Web',
  description:
    'Address autocomplete, forward and reverse geocoding, store locator and live patching in a single REST API, covering 246 countries.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: `First ${formatQuota()} requests per month are free, then pay per request.`,
    url: absolute('/pricing'),
  },
  provider: { '@id': ORGANIZATION_ID },
});

/**
 * BlogPosting node for a DEV.to article.
 * @arg {object} article DEV.to article payload.
 * @return {object}
 */
export const blogPosting = (article) => ({
  '@type': 'BlogPosting',
  '@id': absolute(`/blog/articles/${article.slug}`),
  headline: article.title,
  description: article.description ?? null,
  image: article.social_image || article.cover_image || absolute('/placekit-opengraph.png'),
  datePublished: article.published_at,
  dateModified: article.edited_at || article.published_at,
  author: {
    '@type': 'Person',
    name: article.user?.name ?? null,
    url: article.user?.username ? `https://dev.to/${article.user.username}` : null,
  },
  publisher: { '@id': ORGANIZATION_ID },
  mainEntityOfPage: absolute(`/blog/articles/${article.slug}`),
  keywords: Array.isArray(article.tags) ? article.tags.join(', ') : (article.tags ?? null),
  inLanguage: 'en',
});

/**
 * BreadcrumbList node.
 * @arg {Array<{name: string, path: string}>} items Ordered crumbs.
 * @return {object}
 */
export const breadcrumbs = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: absolute(item.path),
  })),
});

/**
 * FAQPage node.
 * @arg {Array<{question: string, answer: string}>} items Q/A pairs (answer may contain HTML).
 * @arg {string} [locale='en'] Language of the questions and answers.
 * @return {object}
 */
export const faqPage = (items, locale = 'en') => ({
  '@type': 'FAQPage',
  inLanguage: locale,
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

/**
 * Wrap nodes into a single JSON-LD graph document.
 * @arg {...object} nodes Schema.org nodes.
 * @return {object}
 */
export const graph = (...nodes) => ({
  '@context': 'https://schema.org',
  '@graph': nodes.flat().filter(Boolean),
});
